import * as React from "react";
import { GamePlayer, LetterGuessState, SocketMessageType } from "../../Definitions/Definitions";
import { BaseGameState } from "./GameContext";

interface PlayerState {
    matches: LetterGuessState[];
    wordIndex: number;
    guessCount: number;
};

interface WordResults {
    guesses: string[];
    matches: LetterGuessState[][];
    score: number;
    multiplier: number;
}

interface PlayerResults {
    playerId: string;
    playerName: string;
    words: WordResults[];
    totalWordScore: number;
    timeBonus: number;
    totalScore: number;
}

export enum OriginalGamePowerup {
    None = 0,

    // Sabotages
    HideGuess,
    ColorFlip,

    // Boosts
    Hint,
    ScoreMultiplier
}

interface OriginalGameActivePowerup {
    powerup: OriginalGamePowerup;
    timestamp: number;
    duration: number;
    data: any;
}

interface OriginalGameOpponentPowerup {
    id: string;
    playerId: string;
    powerup: OriginalGamePowerup;
}

interface OriginalGamePowerupEvent {
    id: string;
    message: string;
}

export enum OriginalGamePowerupMode {
    Disabled = 0,
    Default,
    Chaos
}

export interface OriginalGameParameters {
    minutes: number;
    wordLength: number;
    wordCount: number;
    guessCount: number;
    activePowerupMode: OriginalGamePowerupMode;
    powerupDelay: number;
    timeBonusEnabled: boolean;
}

const DEFAULT_PARAMETERS: OriginalGameParameters = {
    minutes: 3,
    wordLength: 5,
    wordCount: 5,
    guessCount: 6,
    activePowerupMode: OriginalGamePowerupMode.Disabled,
    powerupDelay: 3,
    timeBonusEnabled: true
}

interface OriginalGameResults {
    words: string[];
    parameters: OriginalGameParameters;
    players: PlayerResults[];
}

interface OriginalGameWordState {
    guesses: string[];
    matches: LetterGuessState[][];
    solution: string;
    invalidLetters: string[];
    positionLetters: string[];
    matchLetters: string[];
}

interface OriginalGameSpectateInfo {
    playerId: string;
    wordStates: { guesses: string[]; matches: LetterGuessState[][]; score: number; solution: string; guessed: boolean }[];
    wordIndex: number;
    activePowerups: OriginalGameActivePowerup[];
    lastPowerupReset: number;
    selectedPowerup: OriginalGamePowerup;
}

export interface OriginalGameState extends BaseGameState {
    playerStateList: Record<string, PlayerState>;
    gameParameters: OriginalGameParameters;
    gameResults: OriginalGameResults | null;
    gameStartTime: number;
    gameTimeRemaining: number;

    wordStates: OriginalGameWordState[];
    wordsGuessed: number;
    wordIndex: number;
    guessEnabled: boolean;
    lastGuessInvalid: boolean;

    wordBonus: number;
    timeBonus: number;
    currentScore: number;

    currentPowerup: OriginalGamePowerup;
    lastPowerupReset: number;
    activePowerups: OriginalGameActivePowerup[];
    recentPowerupEvents: OriginalGamePowerupEvent[];
    opponentPowerups: OriginalGameOpponentPowerup[];
}

export const getOriginalGamePowerupImageName = (powerup: OriginalGamePowerup) => {
    switch (powerup) {
        default: return "unknown.webp";
        case OriginalGamePowerup.ColorFlip: return "colorflip.png";
        case OriginalGamePowerup.HideGuess: return "blind.png";
        case OriginalGamePowerup.Hint: return "hint.webp";
        case OriginalGamePowerup.ScoreMultiplier: return "2x.png";
    }
}

export const TEST_PLAYER_LIST: GamePlayer[] = [
    { name: "player12345678", isHost: false, isSpectator: true, isCurrent: true, startedAsPlayer: false, id: "1" },
    { name: "player12377128371137", isSpectator: false, isHost: false, isCurrent: false, startedAsPlayer: true, id: "2" },
    { name: "player32222", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "3" },
    { name: "wwwwwwwwwwwwwwwwwwww", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "4" },
    { name: "player3", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "5" },
    { name: "player12377128371137", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "6" },
    { name: "player3", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "7" },
    { name: "player3", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "8" },
    { name: "player3", isHost: false, isSpectator: false, isCurrent: false, startedAsPlayer: true, id: "9" }
]

const TEST_GAME_RESULTS: OriginalGameResults = { words: ["quilt", "foxes", "after", "jaggy", "wrist"], parameters: DEFAULT_PARAMETERS, players: [{ playerId: "1", playerName: "player1", words: [{ guesses: ["aspen", "brain", "quilt"], matches: [[0, 0, 0, 0, 0], [0, 0, 0, 1, 0], [2, 2, 2, 2, 2]], score: 400, multiplier: 1 }, { guesses: ["aspen", "brain", "foxes"], matches: [[0, 1, 0, 2, 0], [0, 0, 0, 0, 0], [2, 2, 2, 2, 2]], score: 600, multiplier: 1 }], totalWordScore: 2810, timeBonus: 1000, totalScore: 3810 }, { playerId: "dhfadh", playerName: "player12377128371137", words: [{ guesses: ["aspen", "brain", "quilt"], matches: [[0, 0, 0, 0, 0], [0, 0, 0, 1, 0], [2, 2, 2, 2, 2]], score: 600, multiplier: 2 }, { guesses: ["aspen", "brain", "foxes"], matches: [[0, 1, 0, 2, 0], [0, 0, 0, 0, 0], [2, 2, 2, 2, 2]], score: 200, multiplier: 4 }], totalWordScore: 1520, timeBonus: 0, totalScore: 1520 }] };

const DEFAULT_GAME_STATE: OriginalGameState = {
    playerList: [],
    localPlayer: null,
    actualLocalPlayer: null,
    hostRoomId: 0,
    gameStarted: false,
    lobbyOpen: true,
    switchingSides: false,

    playerStateList: {},
    gameParameters: DEFAULT_PARAMETERS,
    gameResults: null,
    gameStartTime: 0,
    gameTimeRemaining: 0,

    wordStates: [{ guesses: [""], matches: [], solution: "", invalidLetters: [], positionLetters: [], matchLetters: [] }],
    wordsGuessed: 0,
    wordIndex: 0,
    guessEnabled: true,
    lastGuessInvalid: false,

    wordBonus: 0,
    timeBonus: 0,
    currentScore: 0,

    currentPowerup: OriginalGamePowerup.None,
    lastPowerupReset: 0,
    activePowerups: [],
    recentPowerupEvents: [],
    opponentPowerups: []
}

interface Props {
    sendMessage: (type: SocketMessageType, data: string) => void;
}

export const useOriginalGame = (props: Props) => {
    const [state, setState] = React.useState<OriginalGameState>(DEFAULT_GAME_STATE);

    const handleMessage = (type: SocketMessageType, data: string) => {
        switch (type) {
            default:
            { } break;

            case SocketMessageType.PlayerJoin: {
                const parsedData: GamePlayer = JSON.parse(data);
                setState(prevState => {
                    return {
                        ...prevState,
                        playerList: [...prevState.playerList, parsedData],
                        localPlayer: parsedData.isCurrent ? parsedData : prevState.localPlayer,
                        actualLocalPlayer: parsedData.isCurrent ? parsedData : prevState.actualLocalPlayer
                    }
                });
            } break;

            case SocketMessageType.PlayerLeave: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    const newPlayers = prevState.playerList;
                    const index = newPlayers.findIndex((v) => v.id == parsedData.id);
                    if (index != -1) {
                        if (index != newPlayers.length - 1)
                            newPlayers[index] = newPlayers.pop() ?? newPlayers[0];
                        else
                            newPlayers.pop()

                        // if the player we are spectating leaves, start spectating the first non-spectator player
                        if (prevState.actualLocalPlayer?.isSpectator && prevState.localPlayer?.id == parsedData.id) {
                            const player = newPlayers.find(e => !e.isSpectator);
                            if (player)
                                props.sendMessage(SocketMessageType.SpectatePlayer, player.id);
                        }

                        return {
                            ...prevState,
                            playerList: [...newPlayers],
                        }
                    }

                    return prevState;
                });
            } break;

            case SocketMessageType.RoomCreated: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    return {
                        ...prevState,
                        hostRoomId: parsedData.roomId
                    }
                });
            } break;

            case SocketMessageType.GameParametersChanged: {
                const parsedData: OriginalGameParameters = JSON.parse(data);
                setState(prevState => {
                    return {
                        ...prevState,
                        gameParameters: parsedData,
                    }
                });
            } break;

            case SocketMessageType.GameStarted: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    // if we are spectating, start spectating the first non-spectator player
                    if (prevState.actualLocalPlayer?.isSpectator) {
                        const player = prevState.playerList.find(e => !e.isSpectator);
                        if (player)
                            props.sendMessage(SocketMessageType.SpectatePlayer, player.id);
                    }

                    // populate the state list with all originally non spectator players
                    const newStates: Record<string, PlayerState> = {}
                    if (prevState.actualLocalPlayer?.isSpectator && "initialStates" in parsedData) { // use the values sent with initial data if available
                        parsedData.initialStates.forEach((e: any) => {
                            newStates[e.playerId] = {
                                matches: e.matches,
                                wordIndex: e.wordIndex,
                                guessCount: e.guessCount
                            };
                        });
                    } else { // default values
                        prevState.playerList.forEach((e) => {
                            if (e.startedAsPlayer) {
                                newStates[e.id] = {
                                    matches: [],
                                    wordIndex: 0,
                                    guessCount: 0
                                };
                            }
                        });
                    }

                    return {
                        ...prevState,
                        localPlayer: prevState.actualLocalPlayer,
                        lobbyOpen: false,
                        gameStarted: true,
                        wordStates: [{ guesses: [""], matches: [], solution: "", invalidLetters: [], positionLetters: [], matchLetters: [] }],
                        wordsGuessed: 0,
                        wordIndex: 0,
                        playerStateList: newStates,
                        guessEnabled: true,
                        wordBonus: 0,
                        timeBonus: 0,
                        currentScore: 0,
                        gameParameters: parsedData.parameters,
                        gameResults: null,
                        gameTimerCounter: 0,
                        gameStartTime: parsedData.timestamp,
                        gameTimeRemaining: 60 * parsedData.parameters.minutes,
                        currentPowerup: parsedData.initialPowerup,
                        lastPowerupReset: parsedData.timestamp,
                        activePowerups: [],
                        recentPowerupEvents: [],
                        opponentPowerups: []
                    }
                });

                // reset scroll position of divs
                let scrollElem = document.getElementById("opponentScroll");
                if (scrollElem) scrollElem.scrollTop = 0;

                scrollElem = document.getElementById("resultsScroll");
                if (scrollElem) scrollElem.scrollLeft = 0;
            } break;

            case SocketMessageType.GameEnded: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    // once the game ends, if we were intially a player, automatically switch back to the players team
                    if (prevState.actualLocalPlayer?.startedAsPlayer)
                        props.sendMessage(SocketMessageType.JoinPlayers, "");

                    return {
                        ...prevState,
                        gameResults: parsedData,
                        gameStarted: false,
                    }
                });
            } break;

            case SocketMessageType.GuessFeedback: {
                const parsedData = JSON.parse(data);
                if (parsedData.valid) {
                    setState(prevState => {
                        const newWordStates = prevState.wordStates;
                        const wordIndex = parsedData.wordIndex;
                        const guess = parsedData.guess;
                        const isSpectator = prevState.actualLocalPlayer?.isSpectator;

                        // add a new state if the word index is out of range
                        if (isSpectator && wordIndex >= newWordStates.length)
                            newWordStates.push({ guesses: [], matches: [], solution: "", invalidLetters: [], positionLetters: [], matchLetters: [] });

                        const wordState = newWordStates[wordIndex];
                        wordState.solution = parsedData.solution;

                        let newWordIndex = prevState.wordIndex;
                        let newCurrentScore = prevState.currentScore;
                        if (isSpectator) {
                            // if the player is on the most recent word when a word is completed, automatically move to the next
                            if (prevState.wordIndex == wordIndex - 1 && wordState.guesses.length == 0)
                                newWordIndex = wordIndex;

                            // update the total score once we move onto the next word
                            if (wordState.guesses.length == 0)
                                newCurrentScore += prevState.wordBonus;

                            wordState.guesses.push(guess);
                        }

                        let wordsGuessed = prevState.wordsGuessed;
                        if (parsedData.matches.every((e: LetterGuessState) => e == LetterGuessState.Match)) // word guessed
                            wordsGuessed += 1;
                        else if (wordState.solution == "" && !isSpectator) // still guesses remaining
                            wordState.guesses.push("");

                        for (let i = 0; i < parsedData.matches.length; i++) {
                            if (parsedData.matches[i] == LetterGuessState.Match)
                                wordState.matchLetters.push(guess[i]);
                            if (parsedData.matches[i] == LetterGuessState.CorrectLetter)
                                wordState.positionLetters.push(guess[i]);
                            else
                                wordState.invalidLetters.push(guess[i]);
                        }

                        wordState.matches.push(parsedData.matches);

                        return {
                            ...prevState,
                            wordIndex: newWordIndex,
                            wordStates: [...newWordStates],
                            wordsGuessed: wordsGuessed,
                            wordBonus: parsedData.wordBonus,
                            currentScore: newCurrentScore,
                            guessEnabled: true
                        }
                    });
                } else {
                    setState(prevState => {
                        setTimeout(() => {
                            setState(prevState => {
                                return {
                                    ...prevState,
                                    lastGuessInvalid: false
                                }
                            });
                        }, 200);

                        return {
                            ...prevState,
                            guessEnabled: true,
                            lastGuessInvalid: true
                        }
                    });
                }
            } break;

            case SocketMessageType.PlayerGuess: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    if (prevState.localPlayer && prevState.localPlayer.id != parsedData.playerId) {
                        let newStateList = prevState.playerStateList;
                        newStateList[parsedData.playerId] = {
                            ...newStateList[parsedData.playerId],
                            matches: parsedData.matches,
                            wordIndex: parsedData.wordIndex,
                            guessCount: parsedData.guessCount
                        };

                        // move the player to the top of the playerlist
                        let newPlayers = prevState.playerList;
                        newPlayers.forEach((item, i) => {
                            if (item.id == parsedData.playerId) {
                                newPlayers.splice(i, 1);
                                newPlayers.unshift(item);
                            }
                        });

                        return {
                            ...prevState,
                            playerStateList: { ...newStateList },
                            playerList: [...newPlayers]
                        }
                    }

                    return prevState;
                });
            } break;

            case SocketMessageType.PowerupUsed: {
                const parsedData = JSON.parse(data);

                // generate a random id to associate with this event
                const randomId = Math.random().toString().substr(2, 8);

                setState(prevState => {
                    if (state.localPlayer && state.localPlayer.id == parsedData.target) { // powerup affects local player
                        const newEntry: OriginalGameActivePowerup = {
                            timestamp: parsedData.timestamp,
                            powerup: parsedData.powerup,
                            duration: parsedData.duration,
                            data: parsedData.data // only exists when localPlayer is the target
                        };

                        let eventString = "";
                        switch (newEntry.powerup) {
                            default: break;
                            case OriginalGamePowerup.ColorFlip:
                                eventString = `WHAT COLOR WAS THAT? (${newEntry.duration}s)`;
                                break;
                            case OriginalGamePowerup.HideGuess:
                                eventString = `MISSING SOMETHING?? (${newEntry.duration}s)`;
                                break;

                            case OriginalGamePowerup.Hint:
                                eventString = `TAKE A HINT! (${newEntry.duration}s)`;
                                break;
                            case OriginalGamePowerup.ScoreMultiplier:
                                eventString = `SCORE MULTIPLIER ACTIVE (${newEntry.duration}s)`;
                                break;
                        }

                        const eventEntry: OriginalGamePowerupEvent = {
                            id: randomId,
                            message: eventString
                        }

                        if (eventString != "") {
                            setTimeout(() => {
                                setState(prevState => {
                                    let newEvents = prevState.recentPowerupEvents;
                                    const foundIndex = newEvents.indexOf(eventEntry);
                                    if (foundIndex != -1) {
                                        newEvents.splice(foundIndex, 1);
                                        return {
                                            ...prevState,
                                            recentPowerupEvents: [...newEvents]
                                        }
                                    }

                                    return prevState;
                                });
                            }, 2000);
                        }

                        return {
                            ...prevState,
                            activePowerups: [...prevState.activePowerups, newEntry],
                            recentPowerupEvents: eventString != "" ? [...prevState.recentPowerupEvents, eventEntry] : prevState.recentPowerupEvents
                        };
                    } else { // powerup affects another player
                        const newEntry: OriginalGameOpponentPowerup = {
                            id: randomId,
                            playerId: parsedData.target,
                            powerup: parsedData.powerup
                        };

                        setTimeout(() => {
                            setState(prevState => {
                                let newList = prevState.opponentPowerups;
                                const foundIndex = newList.indexOf(newEntry);
                                if (foundIndex != -1) {
                                    newList.splice(foundIndex, 1);
                                    return {
                                        ...prevState,
                                        opponentPowerups: [...newList]
                                    }
                                }

                                return prevState;
                            });
                        }, 2000);

                        return {
                            ...prevState,
                            opponentPowerups: [...prevState.opponentPowerups, newEntry]
                        };
                    }
                });
            } break;

            case SocketMessageType.ResetPowerup: {
                const parsedData = JSON.parse(data);
                setState(prevState => {
                    return {
                        ...prevState,
                        currentPowerup: parsedData.powerup,
                        lastPowerupReset: parsedData.resetTime
                    }
                });
            } break;

            case SocketMessageType.JoinSpectators:
            case SocketMessageType.JoinPlayers: {
                const parsedData: GamePlayer = JSON.parse(data);
                setState(prevState => {
                    const newPlayers = prevState.playerList;
                    const index = newPlayers.findIndex((v) => v.id == parsedData.id);
                    if (index != -1) {
                        newPlayers[index].isSpectator = parsedData.isSpectator;
                        newPlayers[index].startedAsPlayer = parsedData.startedAsPlayer;
                    }

                    return {
                        ...prevState,
                        playerList: [...newPlayers],
                        switchingSides: parsedData.id == prevState.actualLocalPlayer?.id ? false : prevState.switchingSides
                    }
                });
            } break;

            case SocketMessageType.SpectatePlayer: {
                const parsedData: OriginalGameSpectateInfo = JSON.parse(data);
                setState(prevState => {
                    const newPlayerIndex = prevState.playerList.findIndex(e => e.id == parsedData.playerId);
                    if (newPlayerIndex == -1) return prevState;

                    const newPlayer = prevState.playerList[newPlayerIndex];
                    const newWordStates: OriginalGameWordState[] = [];
                    let wordBonus = 0;
                    let currentScore = 0;
                    let wordsGuessed = 0;
                    let wordIndex = parsedData.wordIndex;
                    parsedData.wordStates.forEach(e => {
                        const invalidLetters: string[] = [];
                        const positionLetters: string[] = [];
                        const matchLetters: string[] = [];

                        // populate matched letters
                        for (let i = 0; i < e.guesses.length; i++) {
                            const matches = e.matches[i];
                            const guess = e.guesses[i];
                            for (let j = 0; j < matches.length; j++) {
                                if (matches[j] == LetterGuessState.Match)
                                    matchLetters.push(guess[j]);
                                if (matches[j] == LetterGuessState.CorrectLetter)
                                    positionLetters.push(guess[j]);
                                else
                                    invalidLetters.push(guess[j]);
                            }
                        }

                        const newWordState: OriginalGameWordState = {
                            guesses: e.guesses,
                            matches: e.matches,
                            solution: e.solution,
                            invalidLetters,
                            positionLetters,
                            matchLetters
                        }
                        newWordStates.push(newWordState);

                        wordBonus = e.score;
                        currentScore += e.score;
                        if (e.guessed) wordsGuessed++;
                    });

                    // when spectating a player who has finished, their word index will be larger than the amount of words.
                    // to counteract this on the frontend, we just clamp it to be its max supported value
                    if (wordIndex >= newWordStates.length)
                        wordIndex = newWordStates.length - 1;

                    return {
                        ...prevState,
                        localPlayer: newPlayer,
                        wordStates: newWordStates,
                        wordIndex,
                        wordBonus,
                        wordsGuessed,
                        currentScore: currentScore - wordBonus,
                        activePowerups: parsedData.activePowerups,
                        lastPowerupReset: parsedData.lastPowerupReset,
                        currentPowerup: parsedData.selectedPowerup
                    }
                });
            } break;
        }
    }

    return {
        originalGameState: state,
        updateOriginalGameState: setState,
        originalGameHandleMessage: handleMessage
    }
}